import React from 'react';
import { CheckIcon, PlusIcon, SettingsIcon } from 'lucide-react';
import { ProfileIcon } from '../../../icons/ProfileIcons';
import type { SupportProfile } from '../../../../types/supportProfiles';
type ProfileMenuProps = {
  presets: SupportProfile[];
  saved: SupportProfile[];
  activeId: string | null;
  isCustom: boolean;
  onPick: (profile: SupportProfile) => void;
  onSaveNew: () => void;
  onManage: () => void;
};
type RowProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected?: boolean;
  accent?: boolean;
  role?: 'menuitem' | 'menuitemradio';
  onClick?: () => void;
};
function Row({
  icon,
  title,
  description,
  selected = false,
  accent = false,
  role = 'menuitemradio',
  onClick
}: RowProps) {
  return <button type="button" role={role} aria-checked={role === 'menuitemradio' ? selected : undefined} onClick={onClick} className={`flex w-full items-center gap-[10px] rounded-[6px] px-[8px] py-[6px] text-left transition-colors duration-150 ${accent ? 'hover:bg-pf-blue-soft' : selected ? 'bg-pf-panel' : 'hover:bg-pf-panel'}`} data-id="element-558" data-mp="30vgrcvs1cw63">
      
      <span className={`flex h-[20px] w-[20px] shrink-0 items-center justify-center ${accent ? 'text-pf-blue' : 'text-pf-ink'}`} data-id="element-559" data-mp="2wlazrjub61r9">{icon}</span>
      <span className="min-w-0 flex-1" data-id="element-560" data-mp="36l1r5cnko0n4">
        <span className={`block truncate text-[12.5px] ${accent ? 'text-pf-blue' : 'text-pf-ink'}`} data-id="element-561" data-mp="36k0ej3qwr63a">{title}</span>
        <span className="block truncate text-[11px] text-pf-muted" data-id="element-562" data-mp="28yw5if2vg4d6">{description}</span>
      </span>
      {selected && role === 'menuitemradio' && <CheckIcon className="h-[13px] w-[13px] shrink-0 text-pf-blue" strokeWidth={2.6} data-id="element-563" data-mp="2ivhbp3qmnlil" />}
    </button>;
}
export function ProfileMenu({
  presets,
  saved,
  activeId,
  isCustom,
  onPick,
  onSaveNew,
  onManage
}: ProfileMenuProps) {
  return <div role="menu" aria-label="Support profiles" className="overflow-hidden rounded-[9px] bg-white shadow-[0_8px_28px_rgba(20,28,38,0.2)] ring-1 ring-black/[0.06]" data-id="element-564" data-mp="1q5s7yimj97da">
      <div className="max-h-[min(420px,60vh)] overflow-y-auto p-[5px]" data-id="element-565" data-mp="1n4ytqduh2gzl">
        {isCustom && <Row icon={<CheckIcon className="h-[15px] w-[15px]" strokeWidth={2.6} data-id="element-567" data-mp="1j3vb5jl1i6uo" />} title="Custom (Current)" description="Unsaved changes" selected role="menuitem" data-id="element-566" data-mp="1j3lljx3wq1pi" />}
        {presets.map(p => <Row key={p.id} icon={<ProfileIcon icon={p.icon} className="h-[17px] w-[17px]" data-id="element-569" data-mp="1rx8elfgqcqv0" />} title={p.name} description={p.description} selected={!isCustom && activeId === p.id} onClick={() => onPick(p)} data-id="element-568" data-mp="1rvxf3rubi59p" />)}
        {saved.length > 0 && <>
            <div className="mx-[8px] mb-[4px] mt-[6px] border-t border-pf-line pt-[6px] text-[10.5px] font-semibold text-pf-muted" data-id="element-570" data-mp="1e4sjr1xzfz56">Saved Profiles</div>
            {saved.map(p => <Row key={p.id} icon={<ProfileIcon icon={p.icon} className="h-[17px] w-[17px]" data-id="element-572" data-mp="2pie4mawt7ekw" />} title={p.name} description={p.description} selected={!isCustom && activeId === p.id} onClick={() => onPick(p)} data-id="element-571" data-mp="2pjfiso94gh6u" />)}
          </>}
      </div>
      <div className="border-t border-pf-line p-[5px]" data-id="element-573" data-mp="bc6h7g9w2iyw">
        <Row role="menuitem" accent icon={<PlusIcon className="h-[17px] w-[17px]" strokeWidth={2.4} data-id="element-575" data-mp="3tiukd0ldow1x" />} title="Save Current as Profile…" description="Save all support settings" onClick={onSaveNew} data-id="element-574" data-mp="b9k1uugraz64" />
        
        <Row role="menuitem" icon={<SettingsIcon className="h-[17px] w-[17px]" strokeWidth={2.2} data-id="element-577" data-mp="u264bxm65nzo" />} title="Manage Saved Profiles…" description={saved.length ? `${saved.length} saved · rename, icons, order` : 'Rename, change icons, reorder'} onClick={onManage} data-id="element-576" data-mp="q1chl7kgqf8d" />
        
      </div>
    </div>;
}